import React from 'react';
import {
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip as MUIToolTip,
} from '@material-ui/core';
import { useCubeQuery } from '@cubejs-client/react';
import {
  Area,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import InfoIcon from '@material-ui/icons/Info';
import { HiDownload as DownloadIcon } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { buildUrl, getParams } from 'utils';
import i18n from 'i18n-js';
import {
  CHART_COLORS,
  DEFAULT_CURRENCY,
  dateFormatter,
  xAxisFormatter,
  yAxisFormatter,
  tooltipFormatter,
} from './chartsUtils';
import { useStyles, tooltipStyles } from './Chart.styled';

const Chart = (props) => {
  const { title, x, width, y, height, query, granularity, dates, alias } = props;
  const classes = useStyles();
  const executedQuery = useCubeQuery(query, {
    //
  });
  const locale = useSelector((state) => state?.organizations?.organization?.locale);
  const localeFormat = useSelector((state) => state?.organizations?.localeFormat);
  const formattedLocale = locale && locale.replace('_', '-').toLowerCase();
  const currency = DEFAULT_CURRENCY[formattedLocale] || 'USD';
  return (
    <Paper
      elevation={2}
      style={{
        gridColumnStart: x,
        gridColumnEnd: x + width,
        gridRowStart: y,
        gridRowEnd: y + height,
      }}
      className={classes.chartContainer}
    >
      <ChartRenderer
        height={200}
        {...executedQuery}
        granularity={granularity}
        locale={locale}
        title={title.replace('USD', currency)}
        localeFormat={localeFormat}
        dates={dates}
        alias={alias}
      />
    </Paper>
  );
};

const ChartRenderer = ({
  resultSet,
  error,
  height,
  isLoading,
  progress,
  granularity,
  locale,
  title,
  localeFormat,
  dates,
  alias,
}) => {
  const loading = isLoading;

  if (error) {
    return <div style={{ marginTop: '1.75rem' }}>{error ? error.toString() : ''}</div>;
  }

  return (
    <CartesianChart
      resultSet={resultSet}
      height={height}
      ChartComponent={AreaChart}
      granularity={granularity}
      locale={locale}
      localeFormat={localeFormat}
      title={title}
      dates={dates}
      loading={loading}
      alias={alias}
    />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  granularity,
  localeFormat,
  locale,
  format,
  alias,
}) => {
  const classes = tooltipStyles();
  if (active && payload && payload.length) {
    const labelFormatter = dateFormatter(label, granularity, localeFormat);
    const mapValues = payload.map((item, idx) => (
      <p key={idx} style={{ color: item?.stroke }}>
        {alias && alias[idx] ? `${alias[idx]}: ` : null}
        {tooltipFormatter({ item: item.value, format, locale, idx })}
      </p>
    ));

    return (
      <div className={classes.tooltip}>
        <p className={classes.labelDate}>{labelFormatter}</p>
        <>{mapValues}</>
      </div>
    );
  }

  return null;
};

const CartesianChart = ({
  title,
  resultSet,
  ChartComponent,
  height,
  granularity,
  locale,
  localeFormat,
  dates,
  loading,
  alias,
}) => {
  const classes = useStyles();
  const data = resultSet?.chartPivot();
  const annotation = resultSet?.annotation();

  //If both dates are provided by the ChartList controls, we use them to provide the chart date labels,
  //if not we wait for the data to be available

  const { startDate: firstLabel, endDate: lastLabel } =
    dates.startDate && dates.endDate
      ? dates
      : data?.length
      ? { startDate: data[0]?.x, endDate: data[data.length - 1]?.x }
      : { startDate: '--', endDate: '--' };

  //We are assuming we only use measure and theres only one
  const measures = annotation?.measures;
  const measureNames = measures && Object.keys(measures);
  const format = measures && measures[measureNames[0]]?.format;
  const headers = measures && measureNames.map((measure) => measures[measure].shortTitle);

  const csv =
    data &&
    data.reduce(
      (acc, row) =>
        acc +
        `${row.x.split('.')[0].replace('T', ' ')},${measureNames
          .map((measure) => row[measure]?.toFixed(2))
          .join(',')}\n`,
      `date,${headers.join(',')}\n`
    );

  const downloadInfo = csv && encodeURIComponent(csv);

  const disabled = !downloadInfo;

  return (
    <>
      <div className={classes.chartHeader}>
        <Typography className={classes.title}>{title}</Typography>
        <MUIToolTip title={i18n.t('charts.view_definition')} className={classes.link}>
          <IconButton
            color="primary"
            className={classes.link}
            disabled={disabled}
            // If disabled use div to prevent tooltip from showing warning
            component={disabled ? 'div' : Link}
            to={buildUrl(
              `/reports/docs/aggregate?${measureNames && getParams({ params: measureNames })}`
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            <InfoIcon />
          </IconButton>
        </MUIToolTip>
        <MUIToolTip title={i18n.t('common.download')}>
          <IconButton
            color="primary"
            className={classes.iconButton}
            disabled={disabled}
            // If disabled use div to prevent tooltip from showing warning
            component={disabled ? 'div' : undefined}
            href={downloadInfo && `data:text/csv;charset=utf-8,${downloadInfo}`}
            download={`${title.toLocaleLowerCase().replaceAll(' ', '_')}.csv`}
          >
            <DownloadIcon />
          </IconButton>
        </MUIToolTip>
      </div>
      {loading ? (
        <div className={classes.chartLoading}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <ResponsiveContainer
            width="100%"
            height={height}
            style={{ marginTop: '1.75rem' }}
            debounce={50}
          >
            <ChartComponent
              data={data}
              style={{ overflow: 'visible' }}
              margin={{ top: 5, right: 2, bottom: 5, left: 2 }}
            >
              <XAxis axisLine={false} tickLine={false} tick={false} dataKey="x" />
              <YAxis
                axisLine={false}
                tickLine={false}
                type="number"
                tickFormatter={(item) => yAxisFormatter(item, annotation && format)}
                width={20}
                tick={{ fontSize: '9px' }}
                orientation="right"
              />
              <CartesianGrid />
              {resultSet?.seriesNames().map((series, idx) => (
                <Area
                  key={series.key}
                  stackId="a"
                  dataKey={series.key}
                  name={series.title}
                  stroke={CHART_COLORS[idx]}
                  fill={CHART_COLORS[idx]}
                  fillOpacity={0.5}
                />
              ))}
              <Tooltip
                content={
                  <CustomTooltip
                    granularity={granularity}
                    localeFormat={localeFormat}
                    format={format}
                    locale={locale}
                    alias={alias}
                  />
                }
              />
            </ChartComponent>
          </ResponsiveContainer>
          <div className={classes.chartXLabels}>
            <span>{xAxisFormatter(firstLabel)}</span>
            <span>{xAxisFormatter(lastLabel)}</span>
          </div>
        </>
      )}
    </>
  );
};

export { Chart };
